// Which company a new import is for — the create form's Company select.
//
// A job's company decides who can see it (migration-service 0042): a job
// tagged Kazi is visible to Kazi's people; an untagged ("group-wide") job only
// to people who reach every company. Before this select existed every job the
// Owner created was untagged, so no company-scoped user ever saw one.
//
// The rules the form follows (the backend enforces the same):
//   - options are the caller's migration:write reach, never more;
//   - "All companies (group-wide)" is offered ONLY to someone who reaches every
//     company, and is never the default;
//   - one company ⇒ preselected; several ⇒ no default, a choice is required.
import type { CreatableCompanyReach } from '@/services/migrationService';

/** Select value for the deliberate group-wide (untagged) choice. */
export const GROUP_WIDE = '__group_wide__';

export interface CompanyOption { id: string; name: string }

export interface CompanyChoices {
  companies: CompanyOption[];
  /** The group-wide option is offered (all-companies reach). */
  groupWide: boolean;
  /** Initial select value: '' = nothing chosen yet. */
  initial: string;
}

/** A name for a company id, falling back to a short id when the name is unknown. */
export function companyLabel(id: string, names: Map<string, string>): string {
  return names.get(id) ?? `Company ${id.slice(0, 8)}`;
}

export function companyChoices(reach: CreatableCompanyReach, names: Map<string, string>): CompanyChoices {
  const ids = reach.allCompanies ? [...names.keys()] : reach.legalEntityIds;
  const companies = ids
    .map((id) => ({ id, name: companyLabel(id, names) }))
    .sort((a, b) => a.name.localeCompare(b.name));
  const groupWide = reach.allCompanies;
  let initial = '';
  if (companies.length === 1) initial = companies[0].id;
  // No company to name at all (a tenant with no legal entities): group-wide is
  // the only possible answer for someone who reaches everything.
  else if (companies.length === 0 && groupWide) initial = GROUP_WIDE;
  return { companies, groupWide, initial };
}

/** The legal_entity_id to send for a select value: undefined = group-wide. */
export function legalEntityIdFor(choice: string): string | undefined {
  return choice && choice !== GROUP_WIDE ? choice : undefined;
}
